/*
 * ugc-gallery.js — the homepage video row (sections/video-testimonials.liquid).
 *
 * The section renders a plain horizontally scrollable row of muted clips, so
 * with this file missing the row still scrolls by touch or trackpad and every
 * clip shows its poster. This script adds four things on top:
 *
 *   1. AUTOPLAY ONLY WHAT IS ON SCREEN. Each <video> ships preload="none". An
 *      IntersectionObserver plays a clip once it is mostly in view and pauses
 *      it again when it leaves, so the row never downloads clips nobody sees.
 *   2. AN ENDLESS ROW. The original cards are cloned before and after
 *      themselves. Whenever scrolling settles in a cloned set, the track jumps
 *      by exactly one set width to the same card in the original set, so there
 *      is always more row in both directions. Clones are aria-hidden and inert
 *      so assistive tech and the keyboard only ever meet the originals.
 *   3. PREVIOUS / NEXT BUTTONS, one card per press. They ship `hidden` and are
 *      revealed here, since without this script they would do nothing.
 *   4. A SLOW AUTO-ADVANCE, one card every few seconds, held while the row is
 *      hovered, focused or touched, while it is off screen, and entirely for
 *      visitors who prefer reduced motion.
 */

const ADVANCE_MS = 3500
const SETTLE_MS = 140
const teardowns = new WeakMap()

function initUgcGallery(root) {
  if (root.dataset.ugcGalleryReady === 'true') return
  root.dataset.ugcGalleryReady = 'true'

  const track = root.querySelector('[data-ugc-track]')
  const originals = track ? Array.from(track.querySelectorAll('[data-ugc-card]')) : []
  if (originals.length === 0) return

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

  function makeClone(card) {
    const clone = card.cloneNode(true)
    clone.setAttribute('aria-hidden', 'true')
    clone.setAttribute('inert', '')
    clone.removeAttribute('data-shopify-editor-block')
    return clone
  }

  /*
   * Enough copies on each side that a set is always wider than the viewport
   * and a long swipe cannot run off the end before the jump. Two at minimum.
   */
  const firstPassWidth = Math.max(1, originals[originals.length - 1].offsetLeft + originals[originals.length - 1].offsetWidth - originals[0].offsetLeft)
  const copies = Math.max(2, Math.ceil(track.clientWidth / firstPassWidth) + 1)
  for (let i = 0; i < copies; i += 1) {
    const before = document.createDocumentFragment()
    for (const card of originals) before.appendChild(makeClone(card))
    track.insertBefore(before, track.firstChild)
    for (const card of originals) track.appendChild(makeClone(card))
  }

  const cards = Array.from(track.querySelectorAll('[data-ugc-card]'))

  // Distance from one card to the same card in the next set, gaps included.
  function setWidth() {
    return cards[originals.length].offsetLeft - cards[0].offsetLeft
  }

  function step() {
    return cards[1].offsetLeft - cards[0].offsetLeft
  }

  function jumpTo(left) {
    track.scrollTo({ left, behavior: 'instant' })
  }

  function home() {
    jumpTo(originals[0].offsetLeft - cards[0].offsetLeft)
  }

  /*
   * Called once scrolling has settled. Keeping scrollLeft within half a set of
   * the original set means the copies on either side are always available.
   */
  function recentre() {
    const width = setWidth()
    if (width <= 0) return
    const start = copies * width
    let left = track.scrollLeft
    while (left < start - width / 2) left += width
    while (left >= start + width / 2) left -= width
    if (left !== track.scrollLeft) jumpTo(left)
  }

  let settleTimer = null
  function onScroll() {
    clearTimeout(settleTimer)
    settleTimer = setTimeout(recentre, SETTLE_MS)
  }
  track.addEventListener('scroll', onScroll, { passive: true })

  function move(direction) {
    track.scrollBy({ left: direction * step(), behavior: reducedMotion ? 'instant' : 'smooth' })
  }

  for (const button of root.querySelectorAll('[data-ugc-prev], [data-ugc-next]')) {
    button.hidden = false
    button.addEventListener('click', () => {
      move(button.hasAttribute('data-ugc-next') ? 1 : -1)
      sync()
    })
  }

  // Autoplay: muted is required for it, and set as a property as well as the
  // attribute because some browsers only honour the property on clones.
  const videos = Array.from(track.querySelectorAll('video'))
  let videoObserver = null
  if ('IntersectionObserver' in window) {
    videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target
          if (entry.isIntersecting) video.play()?.catch?.(() => {})
          else video.pause()
        }
      },
      { threshold: 0.6 },
    )
    for (const video of videos) {
      video.muted = true
      videoObserver.observe(video)
    }
  }

  // Auto-advance.
  let held = false
  let onScreen = true
  let timer = null

  function stop() {
    if (timer === null) return
    clearInterval(timer)
    timer = null
  }

  function sync() {
    stop()
    if (reducedMotion || held || !onScreen || document.hidden) return
    timer = setInterval(() => move(1), ADVANCE_MS)
  }

  const hold = () => {
    held = true
    sync()
  }
  const release = () => {
    held = false
    sync()
  }
  root.addEventListener('mouseenter', hold)
  root.addEventListener('mouseleave', release)
  root.addEventListener('focusin', hold)
  root.addEventListener('focusout', release)
  // A touch swipe holds the row until the finger lifts, then it carries on.
  track.addEventListener('touchstart', hold, { passive: true })
  track.addEventListener('touchend', release, { passive: true })
  document.addEventListener('visibilitychange', sync)

  let sectionObserver = null
  if ('IntersectionObserver' in window) {
    sectionObserver = new IntersectionObserver((entries) => {
      onScreen = entries[entries.length - 1].isIntersecting
      sync()
    })
    sectionObserver.observe(root)
  }

  teardowns.set(root, () => {
    stop()
    clearTimeout(settleTimer)
    videoObserver?.disconnect()
    sectionObserver?.disconnect()
    document.removeEventListener('visibilitychange', sync)
  })

  home()
  sync()
}

function initAll(scope) {
  for (const root of scope.querySelectorAll('[data-ugc-gallery]')) initUgcGallery(root)
}

initAll(document)

document.addEventListener('shopify:section:load', (event) => initAll(event.target))

document.addEventListener('shopify:section:unload', (event) => {
  for (const root of event.target.querySelectorAll('[data-ugc-gallery]')) {
    const teardown = teardowns.get(root)
    if (teardown) teardown()
  }
})
